import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import { chapterVolumes } from "@/mocks/home";

const tabs = [
  { key: "all", label: "全部章节" },
  { key: "main", label: "正文卷" },
  { key: "extra", label: "番外篇" },
];

export default function ChapterList() {
  const [tab, setTab] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [expanded, setExpanded] = useState(false);

  const volumes = useMemo(() => {
    const kw = keyword.trim();
    return chapterVolumes
      .filter((volume) => tab === "all" || volume.type === tab)
      .map((volume) => ({
        ...volume,
        chapters: kw
          ? volume.chapters.filter(
              (chapter) => chapter.title.includes(kw) || chapter.summary.includes(kw)
            )
          : volume.chapters,
      }))
      .filter((volume) => volume.chapters.length > 0);
  }, [tab, keyword]);

  const displayVolumes = useMemo(() => {
    const showAll = expanded || keyword.trim().length > 0;
    return volumes.map((volume) => ({
      ...volume,
      chapters: showAll ? volume.chapters : volume.chapters.slice(0, 4),
    }));
  }, [volumes, expanded, keyword]);

  const totalShown = volumes.reduce((sum, volume) => sum + volume.chapters.length, 0);

  return (
    <section id="chapters" className="scroll-mt-24 bg-background-100">
      <div className="mx-auto max-w-6xl px-4 md:px-10 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <Reveal>
            <SectionHeading
              eyebrow="章节目录"
              title="章节列表 · 全文阅读入口"
              desc="按卷整理的章节一览，标题配一句剧情提要。搜索关键词或切换分类，可直接定位到想读的那一章。"
            />
          </Reveal>

          <Reveal delay={100}>
            <div className="w-full lg:w-[340px] flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3">
              <i className="ri-search-line text-lg text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索章节标题或情节关键词"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              {keyword ? (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => setKeyword("")}
                  className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-circle-fill" />
                </button>
              ) : null}
            </div>
          </Reveal>
        </div>

        <Reveal delay={60}>
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                    tab === item.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <span className="text-xs font-label text-foreground-500">
              共匹配 {totalShown} 章
            </span>
          </div>
        </Reveal>

        <div className="mt-8 space-y-10">
          {displayVolumes.map((volume, volumeIndex) => (
            <Reveal key={volume.title} delay={volumeIndex * 70}>
              <div className="flex items-center gap-4">
                <h3 className="font-heading text-lg text-foreground-950">{volume.title}</h3>
                <span className="h-px flex-1 bg-background-300/70" />
                <span className="text-xs font-label text-foreground-500">
                  {volume.chapters.length} 章
                </span>
              </div>

              <ul className="mt-3 divide-y divide-background-200/70">
                {volume.chapters.map((chapter) => (
                  <li key={chapter.no}>
                    <a
                      href="#chapters"
                      className="group flex items-start gap-4 rounded-md px-4 py-4 transition-colors hover:bg-background-50 cursor-pointer"
                    >
                      <span className="w-16 shrink-0 pt-0.5 text-xs font-label text-primary-600">
                        {chapter.no}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-heading text-base text-foreground-900 transition-colors group-hover:text-primary-600">
                            {chapter.title}
                          </span>
                          {chapter.hot ? (
                            <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-label text-accent-800">
                              热门
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-1.5 block text-sm leading-relaxed text-foreground-500">
                          {chapter.summary}
                        </span>
                      </span>
                      <span className="hidden sm:flex shrink-0 flex-col items-end gap-1 pt-0.5">
                        <span className="text-xs font-label text-foreground-400">
                          {chapter.words}
                        </span>
                        <span className="text-[11px] font-label text-foreground-300">
                          {chapter.updated}
                        </span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>

        {displayVolumes.length === 0 ? (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 py-16 text-center">
            <i className="ri-file-search-line text-3xl text-foreground-300" />
            <p className="mt-3 text-sm text-foreground-500">
              没有找到匹配「{keyword}」的章节，换个关键词试试。
            </p>
          </div>
        ) : null}

        <div className="mt-10 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-md border border-foreground-300 px-6 py-3 text-sm font-label text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className={expanded ? "ri-arrow-up-s-line text-base" : "ri-arrow-down-s-line text-base"} />
            {expanded ? "收起部分章节" : "展开更多章节"}
          </button>
          <p className="text-xs font-label text-foreground-400">
            完整版共 1,286 章，本页展示部分章节作为阅读入口
          </p>
        </div>
      </div>
    </section>
  );
}